<template>
  <Form
    ref="observer"
    v-slot="{ meta }"
    class="email-change"
    @submit.prevent
  >
    <!-- Le b-steps sert UNIQUEMENT a afficher le stepper visuel
         ( E-mail / Activation ) . La navigation est piloteee par la
         state machine externe ( useEmailChangeFlow ) , pas par buefy .
         has-navigation="false" supprime le slot navigation interne
         et donne le controle total au composant parent . -->
    <b-steps
      v-model="activeStepIndex"
      :has-navigation="false"
      class="email-change-steps"
    >
      <b-step-item
        label="E-mail"
        icon="envelope"
      >
        <b-message
          class="mt-4 current-email-message"
          has-icon
          size="is-medium"
          type="is-info"
        >
          <p :aria-label="$t('updateUser.info-email') + ' '+ email">
            {{ $t("updateUser.info-email") }} <b>{{ email }}</b>
          </p>
        </b-message>
        <TextField
          v-model:value="flow.password.value"
          type="password"
          label="login.pwd"
          name="password"
          place-holder="login.pwd-placeholder"
          :mandatory="true"
          :disable="flow.isBusy.value"
          horizontal
          @update:value="flow.password.value = $event"
        />
        <TextField
          v-model:value="flow.newEmail.value"
          place-holder="login.email-placeholder"
          :mandatory="true"
          label="updateUser.edit-email"
          name="change_email.new_email"
          rules="email"
          :disable="flow.isBusy.value"
          horizontal
          @update:value="flow.newEmail.value = $event"
        />
        <TextField
          v-model:value="flow.newEmailConfirm.value"
          place-holder="login.email-placeholder"
          label="updateUser.edit-email-confirm"
          name="change_email.new_email_confirmed"
          rules="email|confirmed:change_email.new_email"
          :mandatory="true"
          :disable="flow.isBusy.value"
          horizontal
          @update:value="flow.newEmailConfirm.value = $event"
        />
      </b-step-item>

      <b-step-item
        label="Activation"
        icon="user-pen"
      >
        <b-notification
          :aria-close-label="$t('message.close')"
          type="is-info is-light"
          class="border-notif-info-light"
        >
          <span
            class="column no-break"
            v-html="$t('login.info-mail-validate-key', { email: flow.newEmail.value })"
          />
        </b-notification>
        <!-- Spinner pendant envoi/renvoi de mail ; input cle sinon .
             {@code isResending} cible le cas "renvoyer" depuis cette
             etape Activation ( phase RESENDING distincte de SENDING
             pour eviter un flicker du stepper visuel ) . -->
        <LoadingAnimate
          v-if="flow.isSending.value || flow.isResending.value"
          :size="'is-small'"
        />
        <TextField
          v-else
          v-model:value="flow.verificationKey.value"
          rules="length:12"
          label="login.verificationKey"
          name="change_email.verificationKey"
          place-holder="login.verificationKey-placeholder"
          :mandatory="true"
          :disable="flow.isSubmitting.value"
          @update:value="flow.verificationKey.value = $event"
        >
          <template
            v-if="flow.isInputKey.value"
            #actions
          >
            <a
              class="resend-key-link"
              tabindex="0"
              @click.prevent="flow.resendKey"
              @keyup.enter="flow.resendKey"
            >
              {{ $t("login.resend-validation-key") }}
            </a>
          </template>
        </TextField>
      </b-step-item>
    </b-steps>

    <!-- Footer nav HORS du <b-steps> : sa position est independante de
         la hauteur du contenu de l'etape active . Plus de min-height ,
         plus de padding-top magique , plus de :deep dans les internals
         buefy . Les boutons restent toujours au meme endroit . -->
    <footer class="email-change-footer">
      <div class="nav-left">
        <b-button
          aria-description="missing-password.toSecondStep"
          :disabled="!canGoBack"
          icon-right="chevron-left"
          outlined
          rounded
          @click.prevent="flow.goBack"
        />
        <b-button
          v-if="showNextButton"
          class="btnStepNext"
          :active="meta.valid"
          :disabled="!canRequestKey"
          :loading="flow.isSending.value"
          icon-left="chevron-right"
          type="is-primary"
          rounded
          @click.prevent="flow.requestKey"
        />
      </div>
      <SaveActionButton
        v-if="showSaveButton"
        :active="meta.valid"
        :disabled="!canSubmitKey"
        :loading="flow.isSubmitting.value"
        @click="flow.submitKey"
      />
    </footer>
  </Form>
</template>

<script lang="ts">
import TextField from "@/components/forms/TextField.vue";
import { Form } from "vee-validate";
import { BButton, BMessage, BNotification, BStepItem, BSteps } from "buefy";
import LoadingAnimate from "@/components/common/LoadingAnimate.vue";
import SaveActionButton from "@/components/common/buttons/SaveActionButton.vue";
import { computed, ref } from "vue";
import i18n from "@/i18n";
import { useEmailChangeFlow } from "@/composable/users/useEmailChangeFlow";
import type { VeeForm } from "@/types/vee-validate";
import { useFieldErrorReset } from "@/composable/forms/useFieldErrorReset";
import { useCrossFieldConfirmation } from "@/composable/forms/useCrossFieldConfirmation";

export default {
  name: "EditEmail",
  components: {
    BSteps,
    BStepItem,
    BNotification,
    LoadingAnimate,
    BMessage,
    BButton,
    TextField,
    Form,
    SaveActionButton,
  },
  props: {
    login: {
      type: String,
      required: true,
    },
    email: {
      type: String,
      required: true,
    },
  },
  emits: ["changed-email"],
  setup(props, { emit }) {
    /**
     * Ref vers la balise {@code <Form ref="observer">} de vee-validate .
     * Permet d'appeler {@code setFieldError(field, message)} pour
     * marquer un champ en rouge depuis une erreur de validation
     * cote serveur ( ex 422 BAD_VALIDATION_KEY ) .
     */
    const observer = ref<VeeForm | null>(null);

    const flow = useEmailChangeFlow({
      login: props.login,
      onChanged: (response) => emit("changed-email", response),
      onValidationKeyRejected: () => {
        // Variante "-inline" sans hint action : le bouton "Renvoyer une
        // nouvelle clé par email" est deja visible juste a droite du champ ;
        // dupliquer le hint dans le label rouge sous l'input alourdit
        // visuellement et risque de wrap sur 2 lignes . Le toast verbose
        // ( cle racine ) reste la source d'information principale .
        observer.value?.setFieldError(
            "change_email.verificationKey",
            i18n.global.t("exceptionMessage.BAD_VALIDATION_KEY-inline"),
        );
      },
      // Reset vee-validate apres un commit reussi : sans ce hook , la
      // {@code <Field>} interne garde sa propre {@code value} ; les
      // inputs apparaitraient encore remplis ( avec coche verte de
      // validation ) au retour sur l'onglet apres un email-change OK .
      onResetForm: () => observer.value?.resetForm(),
      onCurrentPasswordRejected: () => {
        observer.value?.setFieldError(
            "password",
            i18n.global.t("exceptionMessage.BAD_CURRENT_PASSWORD"),
        );
      },
      // Nettoie l'eventuelle erreur "Le mot de passe saisi est incorrect"
      // posee par une tentative anterieure quand cette tentative-ci n'a
      // PAS rejete le password ( succes 200 OU erreur 422 portant sur
      // l'email ) . Sans ce nettoyage , le label rouge persiste tant que
      // l'utilisateur n'a pas retape le password ( watcher
      // {@link useFieldErrorReset} ne se declenche que sur mutation ) .
      onCurrentPasswordValid: () => {
        observer.value?.setFieldError("password", "");
      },
    });

    // Reset auto des erreurs server-side au retype - evite que les
    // messages d'erreur de tentatives precedentes ( BAD_CURRENT_PASSWORD
    // sur password , BAD_VALIDATION_KEY sur verificationKey ) persistent
    // visuellement alors que les champs sont corriges .
    useFieldErrorReset(flow.password, observer, "password");
    useFieldErrorReset(flow.verificationKey, observer, "change_email.verificationKey");
    // Re-valide le champ "confirmation" a chaque keystroke sur le champ
    // source : sinon la regle {@code confirmed:change_email.new_email}
    // ne se reevalue pas tant que l'utilisateur ne touche pas a la
    // confirmation - resultat : confirmation marquee verte alors qu'elle
    // diverge de la source recemment modifiee .
    useCrossFieldConfirmation(flow.newEmail, observer, "change_email.new_email_confirmed");

    /**
     * Index du stepper visuel ( v-model b-steps ) derive de la phase
     * de la state machine . Writable mais le setter est intentionnellement
     * un no-op : la navigation est pilotee EXCLUSIVEMENT par les
     * transitions de phase ( {@code flow.requestKey} , {@code flow.goBack} ) .
     * Buefy ecrit sur ce v-model quand l'utilisateur clique directement
     * sur l'en-tete d'une etape ; on ignore pour eviter des sauts d'etat
     * incoherents ( arriver en step 1 sans avoir envoye de mail ) .
     *
     * <p>Regle critique : on bascule sur l'onglet "Activation" UNIQUEMENT
     * apres confirmation backend de l'envoi du mail ( phase {@code INPUT_KEY}
     * ou suivante ) . En phase {@code SENDING} on reste sur "E-mail" avec
     * un spinner sur le bouton next - sinon flicker visible ( aller-retour
     * E-mail -> Activation -> E-mail ) en cas d'erreur backend ex 422 .
     */
    const activeStepIndex = computed<number>({
      get: () => (flow.isInputKey.value || flow.isSubmitting.value || flow.isResending.value ? 1 : 0),
      set: () => { /* navigation pilotee par flow.* , ignore les writes externes */ },
    });

    const canRequestKey = computed(() =>
      flow.newEmail.value.length > 0
        && flow.newEmail.value === flow.newEmailConfirm.value
        && flow.password.value.length > 0
        && !flow.isBusy.value,
    );
    const canSubmitKey = computed(() =>
      flow.isInputKey.value
        && flow.verificationKey.value.length === 12,
    );
    const canGoBack = computed(() =>
      !flow.isCollect.value && !flow.isBusy.value,
    );
    /**
     * Visibilite des boutons de navigation : alignee strictement sur la
     * meme logique que {@link activeStepIndex} pour eviter qu'un bouton
     * "save" apparaisse pendant le SENDING ( spinner phase 1 ) ou qu'un
     * "next" disparaisse au mauvais moment .
     *
     * <ul>
     *   <li>{@code showNextButton} = phase {@code COLLECT} ou {@code SENDING}
     *       ( visible pendant l'envoi pour porter le spinner ) ;</li>
     *   <li>{@code showSaveButton} = phase {@code INPUT_KEY} , {@code SUBMITTING}
     *       ou {@code DONE} ( apres confirmation backend de l'envoi du mail ) .</li>
     * </ul>
     */
    const showNextButton = computed(() =>
      flow.isCollect.value || flow.isSending.value,
    );
    const showSaveButton = computed(() =>
      flow.isInputKey.value || flow.isSubmitting.value || flow.isResending.value,
    );

    return {
      observer,
      flow,
      activeStepIndex,
      canRequestKey,
      canSubmitKey,
      canGoBack,
      showNextButton,
      showSaveButton,
    };
  },
};
</script>

<style scoped lang="scss">
/*
 * Card de changement d'email : flex column pour que le footer ( nav )
 * se positionne naturellement sous le stepper , independamment du
 * contenu de l'etape active . Aucun min-height arbitraire , aucun
 * :deep dans les internals buefy , aucun padding magique .
 */
.email-change {
  display: flex;
  flex-direction: column;
}
/*
 * Compactage vertical du message d'info "Votre email actuel" : buefy
 * applique 1.25em 1.5em par defaut sur {@code .message-body} ce qui
 * cree une bande tres haute pour une seule ligne de texte . On reduit
 * le padding vertical a 0.2em tout en gardant le padding horizontal
 * standard pour l'icone et le retrait du texte . Cible :deep car
 * {@code .message-body} est dans le DOM enfant de {@code <b-message>} .
 */
:deep(.current-email-message .message-body) {
  padding: 0.2em 1.5em;
}
.email-change-steps {
  flex: 1 1 auto;
}
.email-change-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 0.5rem;
  width: 100%;
}
.email-change-footer .nav-left {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}
.resend-key-row {
  margin-top: 0.5rem;
  text-align: right;
}
.resend-key-link {
  font-size: 0.875rem;
  cursor: pointer;
  color: var(--accent-color, #2e8a8a);
  text-decoration: underline;
}
.resend-key-link:hover,
.resend-key-link:focus {
  text-decoration: none;
  opacity: 0.85;
}
</style>
